/**
 * Unified avatar caching and preloading system.
 * Handles Gravatar and Google Contact photos with localStorage persistence.
 */

// Cache keys
const AVATAR_CACHE_KEY = "avatar_cache_v1";
const CACHE_EXPIRY_DAYS = 7;

// Types
interface AvatarCacheEntry {
  gravatarUrl: string | null; // null = 404, string = valid URL
  googlePhotoUrl: string | null;
  timestamp: number;
}

interface AvatarCache {
  entries: Record<string, AvatarCacheEntry>; // keyed by email (lowercase)
  version: number;
}

type AvatarUpdateCallback = (email: string, entry: AvatarCacheEntry) => void;

// In-memory cache for faster access
let memoryCache: Record<string, AvatarCacheEntry> = {};
let cacheLoaded = false;
const updateListeners: Set<AvatarUpdateCallback> = new Set();

// SHA256 hash for Gravatar
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Load cache from localStorage
 */
function loadCache(): void {
  if (cacheLoaded) return;

  try {
    const cached = localStorage.getItem(AVATAR_CACHE_KEY);
    if (cached) {
      const data: AvatarCache = JSON.parse(cached);
      const expiryMs = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      const now = Date.now();

      // Filter out expired entries
      for (const [email, entry] of Object.entries(data.entries)) {
        if (now - entry.timestamp < expiryMs) {
          memoryCache[email] = entry;
        }
      }
    }
  } catch {
    // Ignore errors, start with empty cache
  }

  cacheLoaded = true;
}

/**
 * Save cache to localStorage
 */
function saveCache(): void {
  try {
    const data: AvatarCache = {
      entries: memoryCache,
      version: 1,
    };
    localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(data));
  } catch {
    // localStorage might be full, ignore
  }
}

/**
 * Get cached avatar entry for an email
 */
export function getCachedAvatar(email: string): AvatarCacheEntry | null {
  loadCache();
  return memoryCache[email.toLowerCase().trim()] || null;
}

/**
 * Update cache entry and notify listeners
 */
function updateCacheEntry(email: string, updates: Partial<AvatarCacheEntry>): void {
  const normalizedEmail = email.toLowerCase().trim();
  const existing = memoryCache[normalizedEmail] || {
    gravatarUrl: undefined,
    googlePhotoUrl: undefined,
    timestamp: 0,
  };

  const entry: AvatarCacheEntry = {
    ...existing,
    ...updates,
    timestamp: Date.now(),
  };

  memoryCache[normalizedEmail] = entry;
  saveCache();

  // Notify listeners
  updateListeners.forEach((cb) => cb(normalizedEmail, entry));
}

/**
 * Subscribe to avatar cache updates
 */
export function subscribeToAvatarUpdates(callback: AvatarUpdateCallback): () => void {
  updateListeners.add(callback);
  return () => updateListeners.delete(callback);
}

/**
 * Test if a Gravatar image exists by attempting to load it
 */
async function testGravatarImage(hash: string, size: number = 80): Promise<string | null> {
  const url = `https://gravatar.com/avatar/${hash}?d=404&s=${size}`;

  return new Promise((resolve) => {
    const img = new Image();
    img.referrerPolicy = "no-referrer";

    img.onload = () => resolve(url);
    img.onerror = () => resolve(null);

    // Timeout after 5 seconds
    setTimeout(() => resolve(null), 5000);

    img.src = url;
  });
}

/**
 * Preload avatars for a list of emails
 * Runs in background, updates cache as images are loaded
 */
export async function preloadAvatars(
  emails: (string | null)[],
  googlePhotos: Record<string, string> = {},
  options: { concurrency?: number; onProgress?: (loaded: number, total: number) => void } = {}
): Promise<void> {
  loadCache();

  const { concurrency = 5, onProgress } = options;

  // Filter to unique, valid emails that need loading
  const emailsToLoad = [...new Set(emails.filter(Boolean))] as string[];
  const emailsNeedingGravatar: string[] = [];

  // First pass: update Google photos and identify which need Gravatar check
  for (const email of emailsToLoad) {
    const normalizedEmail = email.toLowerCase().trim();
    const cached = memoryCache[normalizedEmail];
    const googlePhoto = googlePhotos[normalizedEmail] || null;

    // Update Google photo if we have one
    if (googlePhoto && (!cached || cached.googlePhotoUrl !== googlePhoto)) {
      updateCacheEntry(email, { googlePhotoUrl: googlePhoto });
    }

    // Check if we need to test Gravatar
    if (!cached || cached.gravatarUrl === undefined) {
      emailsNeedingGravatar.push(email);
    }
  }

  if (emailsNeedingGravatar.length === 0) {
    onProgress?.(emailsToLoad.length, emailsToLoad.length);
    return;
  }

  // Precompute hashes
  const emailHashes = new Map<string, string>();
  await Promise.all(
    emailsNeedingGravatar.map(async (email) => {
      const hash = await sha256(email.toLowerCase().trim());
      emailHashes.set(email, hash);
    })
  );

  // Load Gravatar images with concurrency limit
  let loaded = emailsToLoad.length - emailsNeedingGravatar.length;
  const total = emailsToLoad.length;

  const queue = [...emailsNeedingGravatar];

  async function processNext(): Promise<void> {
    const email = queue.shift();
    if (!email) return;

    const hash = emailHashes.get(email);
    if (!hash) return;

    const gravatarUrl = await testGravatarImage(hash);
    updateCacheEntry(email, { gravatarUrl });

    loaded++;
    onProgress?.(loaded, total);

    // Process next in queue
    await processNext();
  }

  // Start concurrent workers
  const workers = Array(Math.min(concurrency, queue.length))
    .fill(null)
    .map(() => processNext());

  await Promise.all(workers);
}

/**
 * Get the best available avatar URL for an email
 * Returns: { url: string | null, type: 'gravatar' | 'google' | 'none', loading: boolean }
 */
export function getAvatarUrl(email: string | null): {
  url: string | null;
  type: "gravatar" | "google" | "none";
  loading: boolean;
} {
  if (!email) {
    return { url: null, type: "none", loading: false };
  }

  loadCache();
  const cached = getCachedAvatar(email);

  if (!cached) {
    return { url: null, type: "none", loading: true };
  }

  // Prefer Gravatar if available
  if (cached.gravatarUrl) {
    return { url: cached.gravatarUrl, type: "gravatar", loading: false };
  }

  // Fall back to Google photo
  if (cached.googlePhotoUrl) {
    return { url: cached.googlePhotoUrl, type: "google", loading: false };
  }

  // Both checked but neither available
  if (cached.gravatarUrl === null) {
    return { url: null, type: "none", loading: false };
  }

  // Still loading
  return { url: null, type: "none", loading: true };
}

/**
 * Clear the entire avatar cache
 */
export function clearAvatarCache(): void {
  memoryCache = {};
  cacheLoaded = false;
  localStorage.removeItem(AVATAR_CACHE_KEY);
}
